import { HTMLAttributes } from 'react'
import { Box, Container, Grid, SvgIcon, Typography } from '@mui/material'
import parse from 'html-react-parser'
import Link from 'next/link'

import Button from '@/components/base/button'
import Spacer from '@/components/sections/spacer'
import { isHappa } from '@/hooks/use-brand'

import { brand } from '@/config/brand'
import { WPLink, WPMedia } from '@/types'

const gradients = [
  'linear-gradient(212deg, #E6ECF9 0%, #C0CEF0 80.84%)',
  'linear-gradient(212deg, #97ACE6 0%, #6D89DC 80.84%)',
  'linear-gradient(212deg, #C0CEF0 0%, #97ACE6 80.84%)'
]

const buttonsBg = [
  '#6D89DC', // 300
  '#002F97', // 500
  '#4E6FD4' // 400
]

export type RowProps = HTMLAttributes<HTMLDivElement> &
  WPMedia & {
    title: string
    text: string
    link: {
      text: string
      button: WPLink
    }
    is_first?: boolean
    service_card: {
      title: string
      text: string
      link: WPLink
      icon: string
    }[]
  }

const Row = ({ title, text, link, service_card, is_first, ...props }: RowProps) => {
  return (
    <Box {...props}>
      <Container>
        <Grid container>
          <Grid size={{ xs: 4, sm: 8, md: 6 }} offset={{ md: 3 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}
            >
              <Typography
                variant={isHappa ? 'h3' : 'h4'}
                component={is_first ? 'h1' : 'h2'}
                fontWeight={isHappa ? 700 : 400}
                color={isHappa ? 'primary' : '#212121'}
              >
                {title}
              </Typography>
              <Typography variant="textRegular" color="text.primary" component="p">
                {parse(text)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Spacer size="md" />
      <Container>
        <Grid container>
          {(service_card ?? []).map((card, i) => (
            <Grid key={i.toString()} size={{ xs: 4, sm: 4, lg: 3 }}>
              <Box
                sx={(theme) => ({
                  p: { xs: 2, md: 3 },
                  borderRadius: isHappa ? theme.shape.borderRadius2 : theme.shape.borderRadius8,
                  boxShadow: isHappa ? theme.shadows[2] : 'none',
                  background:
                    isHappa && false
                      ? theme.palette.surface.background
                      : gradients[i % gradients.length],
                  height: '100%',
                  aspectRatio: isHappa ? '1 / 1' : 'auto'
                })}
              >
                <Box
                  sx={(theme) => ({
                    p: 2,
                    pb: 3,
                    borderRadius: theme.shape.borderRadius6,
                    background: theme.palette.text.primaryContrastText,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 3,
                    height: '100%'
                  })}
                >
                  {card.icon ? (
                    <Box
                      sx={(theme) => ({
                        background: isHappa
                          ? brand.colors.yellow[500]
                          : buttonsBg[i % buttonsBg.length],
                        color: isHappa ? theme.palette.primary.main : 'white',
                        width: '4rem',
                        height: '4rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '100px'
                      })}
                    >
                      <SvgIcon>
                        <use href={`#ico-${card.icon}`} />
                      </SvgIcon>
                    </Box>
                  ) : null}
                  <Box
                    sx={() => ({
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 2,
                      mt: isHappa ? 'auto' : 0
                    })}
                  >
                    {card.title ? (
                      <Typography
                        variant="h6"
                        component="span"
                        color={isHappa ? 'primary' : '#212121'}
                      >
                        {card.title}
                      </Typography>
                    ) : null}
                    {card.text ? (
                      <Typography variant="textRegular" color="text.secondary" component="p">
                        {card.text}
                      </Typography>
                    ) : null}
                    {card.link?.url ? (
                      <Typography variant="textRegular" color="text.secondary" component="span">
                        {card.link?.target === '_blank' ? (
                          <a href={card.link.url} target="_blank" rel="noopener noreferrer">
                            {card.link.title}
                          </a>
                        ) : (
                          <Link href={card.link.url}>{card.link.title}</Link>
                        )}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
          {link.text ? (
            <Grid size={{ xs: 4, sm: 4, lg: 3 }}>
              <Box
                sx={(theme) => ({
                  p: 3,
                  borderRadius: isHappa ? theme.shape.borderRadius2 : theme.shape.borderRadius8,
                  border: isHappa ? `1px solid rgba(17, 47, 223, 0.20)` : 'none',
                  background: theme.palette.surface.background,
                  boxShadow: theme.shadows[2],
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                })}
              >
                {link.text ? <Typography variant="h6">{link.text}</Typography> : null}
                {link.button?.url ? (
                  <Button
                    href={link.button.url}
                    variant="contained"
                    color={isHappa ? 'primary' : 'info'}
                    icon="plus"
                  >
                    {link.button.title}
                  </Button>
                ) : null}
              </Box>
            </Grid>
          ) : null}
        </Grid>
      </Container>
    </Box>
  )
}

export default Row
